import { c as _c } from "react/compiler-runtime";
import { FormHelperErrorText, Tooltip } from "@flat-ui";
import { isValidTeamMembersSearchQuery, MIN_CHARACTERS_FOR_TEAM_MEMBERS_SEARCH, TEAM_DISPLAY_NAME } from "@src/constants/team-constants";
import { withEllipsis } from "@styles/common-styles";
import { isValidProjectSearchQuery, MIN_CHARACTERS_FOR_PROJECT_SEARCH } from "@utils/project-utils";
import { SearchbarEvents } from "@utils/track-event/track-event-list";

/** Configuration of the search bar */

/**
 * Creates a validated search config (validator + error message) for minimum-length search constraints.
 * @param isValidSearch Predicate that validates the search string (should return true for valid search or empty string)
 * @param minCharacters Minimum number of characters required for a valid search
 * @returns Object with validator and errorMessage to spread into SearchbarConfig
 */
function createMinLengthSearchConfig(isValidSearch, minCharacters) {
  return {
    validator: search => search.length === 0 || isValidSearch(search),
    errorMessage: `At least ${minCharacters} characters required.`
  };
}

/** Configuration of the search bar for each route/page */
export const SEARCHBAR_CONFIG = {
  projects: {
    isSearchAllowed: true,
    placeholder: "Search for project",
    logEventParams: {
      name: SearchbarEvents.searchForProject
    },
    ...createMinLengthSearchConfig(isValidProjectSearchQuery, MIN_CHARACTERS_FOR_PROJECT_SEARCH)
  },
  analytics: {
    isSearchAllowed: true,
    placeholder: "Search for project",
    logEventParams: {
      name: SearchbarEvents.searchForProject
    }
  },
  members: {
    isSearchAllowed: true,
    placeholder: "Search for member",
    logEventParams: {
      name: SearchbarEvents.searchForWorkspaceMember
    }
  },
  groups: {
    isSearchAllowed: true,
    placeholder: "Search for group",
    logEventParams: {
      name: SearchbarEvents.searchForGroup
    }
  },
  groupMembers: {
    isSearchAllowed: true,
    placeholder: "Search for member",
    logEventParams: {
      name: SearchbarEvents.searchForGroupMember
    }
  },
  groupProjects: {
    isSearchAllowed: true,
    placeholder: "Search for project",
    logEventParams: {
      name: SearchbarEvents.searchForGroupProject
    },
    ...createMinLengthSearchConfig(isValidProjectSearchQuery, MIN_CHARACTERS_FOR_PROJECT_SEARCH)
  },
  workspaces: {
    isSearchAllowed: true,
    placeholder: "Search for workspace",
    logEventParams: {
      name: SearchbarEvents.searchForWorkspace
    }
  },
  teams: {
    isSearchAllowed: true,
    placeholder: `Search for ${TEAM_DISPLAY_NAME}`,
    logEventParams: {
      name: SearchbarEvents.searchForTeam
    }
  },
  snapshots: {
    isSearchAllowed: true,
    placeholder: "Search for snapshot",
    logEventParams: {
      name: SearchbarEvents.searchForSnapshot
    }
  },
  projectTeam: {
    isSearchAllowed: true,
    placeholder: "Search for member",
    logEventParams: {
      name: SearchbarEvents.searchForProjectMember
    }
  },
  teamMembers: {
    isSearchAllowed: true,
    placeholder: "Search for member",
    logEventParams: {
      name: SearchbarEvents.searchForTeamMember
    },
    ...createMinLengthSearchConfig(isValidTeamMembersSearchQuery, MIN_CHARACTERS_FOR_TEAM_MEMBERS_SEARCH)
  },
  projectDetails: {},
  account: {},
  hbdSettings: {},
  subscriptionExpired: {},
  unknown: {}
};

/** Default error message to display in the searchbar error helper */
const DEFAULT_ERROR_MESSAGE = "Invalid input.";
/** Returns the error message to display next to the search field when the search input is invalid */
export function SearchbarErrorMsg(t0) {
  const $ = _c(6);
  const {
    errorMessage
  } = t0;
  const t1 = errorMessage ?? DEFAULT_ERROR_MESSAGE;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      mt: 0,
      ...withEllipsis
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const t3 = errorMessage ?? DEFAULT_ERROR_MESSAGE;
  let t4;
  if ($[1] !== t3) {
    t4 = <FormHelperErrorText sx={t2}>{t3}</FormHelperErrorText>;
    $[1] = t3;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  let t5;
  if ($[3] !== t1 || $[4] !== t4) {
    t5 = <Tooltip component="div" title={t1}>{t4}</Tooltip>;
    $[3] = t1;
    $[4] = t4;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  return t5;
}